import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createIntake, addCompanyDoc, fetchMetaOptions, getDocKinds, getDocStatuses, getLegalForms, getSectors, fetchJSON, API } from "../api";
import { useNotify } from "../components/Notify.jsx";
import { resolveExpiryPolicy } from "../lib/expiryPolicy.js";
import ErrorBoundary from "../components/ErrorBoundary.jsx";

/* Wizard steps */
const STEPS = ["ENTITY", "UBO'S", "DOCUMENTS", "REVIEW & SUBMIT"];

/* Storage key for auto-save */
const INTAKE_STORAGE_KEY = "intake_draft";
const AUTOSAVE_DELAY = 3000; // 3 seconds

// --- UBO search behavior (Google-y) ---
const UBO_SEARCH_DEBOUNCE = 350; // ms
const MIN_FIRST_OR_LAST = 2;
const MIN_COMBINED = 3;

/* Small field wrapper */
function Field({ label, children, hint }) {
  return (
    <label className="f-field">
      <div className="f-label">{label}</div>
      {children}
      {hint ? (
        <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
          {hint}
        </div>
      ) : null}
    </label>
  );
}

/* UBO Suggestion Modal */
function UboSuggestionModal({ isOpen, suggestions, onClose, onUseExisting, onCreateNew }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h4>Similar person found</h4>
        <p>We found similar people in our database:</p>
        {suggestions.map((person, idx) => (
          <div key={idx} className="suggestion-item" style={{ padding: 8, border: "1px solid #ccc", margin: "4px 0" }}>
            <strong>
              {person.first_name} {person.last_name}
            </strong>{" "}
            ({person.country_iso2})<div className="text-muted">ID: {person.id}</div>
            <button type="button" onClick={() => onUseExisting(person)} style={{ marginTop: 4 }}>
              Use this person
            </button>
          </div>
        ))}
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button type="button" onClick={onCreateNew}>
            Create new person anyway
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* Save Status Indicator */
function SaveStatus({ status }) {
  const statusConfig = {
    saving: { text: "Saving...", color: "#666" },
    saved: { text: "Saved", color: "#0a7c0a" },
    unsaved: { text: "Unsaved changes", color: "#b8860b" },
    error: { text: "Save failed", color: "#cc4444" },
  };

  const config = statusConfig[status] || statusConfig.unsaved;

  return <div style={{ fontSize: 12, color: config.color, padding: "4px 8px" }}>{config.text}</div>;
}

/* ---------- Safe option helpers (prevents crashes if arrays contain objects) ---------- */
function safeText(x) {
  if (x == null) return "";
  if (typeof x === "string" || typeof x === "number") return String(x);
  return (
    x.label ||
    x.name ||
    x.key ||
    x.value ||
    (() => {
      try {
        return JSON.stringify(x);
      } catch {
        return "[object]";
      }
    })()
  );
}
function safeValue(x) {
  if (x == null) return "";
  if (typeof x === "string" || typeof x === "number") return String(x);
  return x.value || x.key || x.label || x.name || "";
}
function safeKey(x, i) {
  if (x == null) return `null-${i}`;
  if (typeof x === "string" || typeof x === "number") return String(x);
  return x.key || x.value || x.label || x.name || `opt-${i}`;
}
function safeOption(x, i, context = "option") {
  if (x == null) {
    console.warn(`Intake: null/undefined ${context} at index`, i);
    return null;
  }
  const key = safeKey(x, i);
  const value = safeValue(x);
  const label = safeText(x);
  if (typeof x !== "string" && typeof x !== "number" && typeof x !== "object") {
    console.warn(`Intake: unsupported ${context} type`, typeof x, x);
  }
  return (
    <option key={key} value={value}>
      {label}
    </option>
  );
}
function toStringList(list) {
  return Array.from(new Set((list || []).map((x) => safeText(x)).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

// Normalize backend lists → [{ key, label, ...rest }]
function normalizeOptions(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (item == null) return null;
      if (typeof item === "string" || typeof item === "number") {
        const v = String(item);
        return { key: v.toLowerCase(), label: v };
      }
      const key =
        item.key ??
        item.value ??
        (item.label ? String(item.label).toLowerCase().replace(/\./g, "_").replace(/\s+/g, "_") : null) ??
        (item.name ? String(item.name).toLowerCase().replace(/\./g, "_").replace(/\s+/g, "_") : null);

      const label = item.label ?? item.name ?? item.key ?? item.value ?? safeText(item);
      return {
        ...item,
        key: key || safeKey(item, 0),
        label,
      };
    })
    .filter(Boolean);
}

// Normalize docKinds → [{ key, label, target, ...rest }]
function normalizeDocKinds(rawDocKinds) {
  const list = normalizeOptions(rawDocKinds);
  return list.map((k) => ({
    target: k.target || k.scope || "both",
    ...k,
  }));
}

// Helper to lookup labels in meta on Review step
function findLabelInList(list, key) {
  if (!key) return "";
  const hit = (list || []).find((item) => item && (item.key === key || item.value === key));
  return hit?.label || key;
}

// Compute allowed doc kinds per target
function getAllowedDocKinds(allKinds, target) {
  if (!Array.isArray(allKinds) || !allKinds.length) return [];
  return allKinds.filter((kind) => {
    const t = (kind.target || "both").toLowerCase();
    if (target === "company") return t === "company" || t === "both";
    if (target === "ubo") return t === "ubo" || t === "person" || t === "both";
    return true;
  });
}

/* Helpers */
function stripEmpty(obj) {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  });
  return out;
}

const emptyUbo = () => ({
  first_name: "",
  last_name: "",
  country_iso2: "",
  share_pct: "",
  start_date: "",
  end_date: "",
  _is_existing: false,
  _existing_id: null,
});

const emptyDoc = (kind = "passport", target = "company") => ({
  target, // company | ubo
  ubo_index: 0, // when target === 'ubo'
  kind: target === "company" ? (kind === "passport" ? "certificate_of_incorporation" : kind) : kind,
  identifier: "",
  issued_on: "",
  expires_on: "",
  period_from: "",
  period_to: "",
  authority: "",
  status: "",
  kind_label: "",
  note: "",
  file: null,
});

/* ===== Inline validation helpers (soft, "Google-y") ===== */
function isNum(n) {
  return n !== "" && n != null && !Number.isNaN(Number(n));
}

function validateUbos(ubos) {
  const errors = [];
  let total = 0;
  ubos.forEach((u, i) => {
    const any = Object.values(u).some((v) => String(v || "").trim() !== "");
    if (!any) return;
    if (!u.first_name || !u.last_name) errors.push({ section: "UBOs", message: `UBO #${i}: first and last name are required` });
    if (!u.country_iso2) errors.push({ section: "UBOs", message: `UBO #${i}: country is required` });
    if (u.share_pct !== "" && u.share_pct != null) {
      if (!isNum(u.share_pct) || Number(u.share_pct) < 0 || Number(u.share_pct) > 100) {
        errors.push({ section: "UBOs", message: `UBO #${i}: share % must be 0–100` });
      } else {
        total += Number(u.share_pct);
      }
    }
    if (u.start_date && u.end_date && new Date(u.start_date) > new Date(u.end_date)) {
      errors.push({ section: "UBOs", message: `UBO #${i}: start date must be ≤ end date` });
    }
  });
  return { errors, total };
}

function validateDocuments(docs, ubos, company, meta) {
  const errors = [];
  docs.forEach((d, i) => {
    const hasAny = Object.entries(d).some(([k, v]) => k !== "file" && v);
    if (!hasAny) {
      errors.push({ section: "Documents", message: `Doc #${i + 1}: is empty, remove it or fill required fields` });
      return;
    }

    if (d.target === "ubo") {
      const idx = Number(d.ubo_index);
      if (!Number.isInteger(idx) || idx < 0 || idx >= ubos.length) {
        errors.push({ section: "Documents", message: `Doc #${i + 1}: invalid UBO index` });
      } else {
        const u = ubos[idx];
        if (!u || (!u.first_name && !u.last_name)) {
          errors.push({
            section: "Documents",
            message: `Doc #${i + 1}: UBO #${idx} has no name yet`,
          });
        }
      }
    }

    const kind = d.kind || "passport";
    const cfg = meta?.docKindFields?.[kind];
    if (!cfg) {
      errors.push({ section: "Documents", message: `Doc #${i + 1}: unknown kind '${kind}'` });
    } else {
      for (const f of cfg.required || []) {
        if (f === "file") continue;
        if (!d[f]) {
          errors.push({
            section: "Documents",
            message: `Doc #${i + 1}: '${f}' is required for '${kind}'`,
          });
        }
      }
    }

    if (kind === "other" && !d.kind_label) {
      errors.push({
        section: "Documents",
        message: `Doc #${i + 1}: 'Specify kind' is required for 'other'`,
      });
    }

    const contextCountry = d.target === "ubo" ? ubos[Number(d.ubo_index)]?.country_iso2 : company.country_iso2;
    const policy = resolveExpiryPolicy(kind, contextCountry, meta?.docExpiryPolicies);
    const dst = policy.field || "expires_on";
    const src = policy.source || "issued_on";
    const from = d[src];
    const to = d[dst];

    if (policy.expires && from && to && new Date(from) > new Date(to)) {
      errors.push({
        section: "Documents",
        message: `Doc #${i + 1}: start/issued must be ≤ end/expires`,
      });
    }
  });
  return errors;
}

export default function Intake() {
  /* ----- meta options (countries, sectors, legal forms, doc kinds/fields/statuses) */
  const [meta, setMeta] = useState({
    countries: [],
    sectors: [],
    legal_forms: [],
    docKinds: [],
    docKindFields: {}, // { [kind]: { required:[], optional:[] } }
    docStatuses: [],
    docExpiryPolicies: {},
  });
  const [loadingMeta, setLoadingMeta] = useState(true);

  /* ----- wizard state */
  const [step, setStep] = useState(0);
  const progress = ((step + 1) / STEPS.length) * 100;

  /* ----- entity */
  const [company, setCompany] = useState({
    name: "",
    alias: "",
    country_iso2: "",
    sector: "",
    sector_other: "",
    legal_form: "",
    legal_form_other: "",
    residency_status: "local",
    managed_by: "gtrust", // ✅ NEW: Default to G-Trust
    registration_no: "",
    phone: "",
    address: "",
    notes: "",
  });

  // multi-email UX
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const notify = useNotify();

  /* ----- UBOs & documents */
  const [ubos, setUbos] = useState([]);
  const [docs, setDocs] = useState([emptyDoc("certificate_of_incorporation", "company")]);

  /* ----- UX & Auto-save */
  const [msg, setMsg] = useState("");
  const [created, setCreated] = useState(null);
  const [uploadStatus, setUploadStatus] = useState([]);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [uboSuggestions, setUboSuggestions] = useState([]);
  const [showUboModal, setShowUboModal] = useState(false);
  const [currentUboIndex, setCurrentUboIndex] = useState(null);

  const autoSaveTimeoutRef = useRef();
  const lastSaveStateRef = useRef("");
  const isSubmittingRef = useRef(false);

  const uboSearchTimersRef = useRef(new Map()); // Map<index, number>
  const uboSearchCacheRef = useRef(new Map()); // Map<key, persons[]>
  const uboSearchSeqRef = useRef(0); // sequence for race safety
  const uboLatestHandledRef = useRef(0); // last accepted response

  // Load meta data
  useEffect(() => {
    let cancelled = false;
    async function loadMeta() {
      setLoadingMeta(true);
      try {
        const [base, docMetaRaw, statuses, legalFormsRaw, sectorsRaw] = await Promise.all([
          fetchMetaOptions().catch(() => ({})),
          getDocKinds().catch(() => ({})),
          getDocStatuses().catch(() => []),
          getLegalForms().catch(() => []),
          getSectors().catch(() => []),
        ]);

        const rawDocKinds = docMetaRaw?.docKinds ?? docMetaRaw?.kinds ?? (Array.isArray(docMetaRaw) ? docMetaRaw : []) ?? [];

        const docKindFields = docMetaRaw?.docKindFields ?? docMetaRaw?.fields ?? {};
        const docExpiryPolicies = docMetaRaw?.docExpiryPolicies ?? docMetaRaw?.expiry_policies ?? docMetaRaw?.expiryPolicies ?? {};

        // ✅ Normalize everything to { key, label, ... }
        const legal_forms = normalizeOptions(legalFormsRaw);
        const sectors = normalizeOptions(sectorsRaw);
        const docKinds = normalizeDocKinds(rawDocKinds);

        if (!cancelled) {
          setMeta({
            countries: base?.countries || [],
            sectors,
            legal_forms,
            docKinds,
            docKindFields: docKindFields || {},
            docStatuses: statuses,
            docExpiryPolicies,
          });
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    }
    loadMeta();
    return () => {
      cancelled = true;
    };
  }, []);

  // ✅ Add cleanup on unmount so no stale draft survives
  useEffect(() => {
    return () => {
      sessionStorage.removeItem(INTAKE_STORAGE_KEY);
    };
  }, []);

  useEffect(() => {
    return () => {
      for (const t of uboSearchTimersRef.current.values()) clearTimeout(t);
      uboSearchTimersRef.current.clear();
    };
  }, []);

  // Load saved draft on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(INTAKE_STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        setCompany(data.company || {});
        setEmails(data.emails || []);
        setUbos(data.ubos || []);
        setDocs(data.docs || [emptyDoc("passport")]);
        setStep(data.step || 0);
        setSaveStatus("saved");
      }
    } catch (e) {
      console.warn("Failed to load draft:", e);
    }
  }, []);

  // Auto-save functionality
  const saveToStorage = useCallback(() => {
    if (isSubmittingRef.current) return; // Don't save during submission

    try {
      const data = {
        company,
        emails,
        ubos,
        docs: docs.map((d) => ({ ...d, file: null })), // Don't save files
        step,
        timestamp: Date.now(),
      };

      const dataStr = JSON.stringify(data);
      if (dataStr !== lastSaveStateRef.current) {
        sessionStorage.setItem(INTAKE_STORAGE_KEY, dataStr);
        lastSaveStateRef.current = dataStr;
        setSaveStatus("saved");
      }
    } catch (e) {
      console.warn("Failed to save draft:", e);
      setSaveStatus("error");
    }
  }, [company, emails, ubos, docs, step]);

  // Debounced auto-save
  useEffect(() => {
    if (saveStatus === "unsaved") {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = setTimeout(() => {
        setSaveStatus("saving");
        saveToStorage(); // call immediately
      }, AUTOSAVE_DELAY);
    }
  }, [saveStatus, company, emails, ubos, docs, step]);

  // Mark as unsaved when data changes
  useEffect(() => {
    if (saveStatus === "saved") {
      setSaveStatus("unsaved");
    }
  }, [company, emails, ubos, docs, step]);

  // Also clear on page unload/refresh if not on intake page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (window.location.pathname !== "/intake") {
        sessionStorage.removeItem(INTAKE_STORAGE_KEY);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // doc kinds enforcement when loaded
  // doc kinds enforcement when loaded
  useEffect(() => {
    if (!meta.docKinds || meta.docKinds.length === 0) return;

    setDocs((prev) =>
      prev.map((d) => {
        const allowed = getAllowedDocKinds(meta.docKinds, d.target);
        const fallbackList = allowed.length ? allowed : meta.docKinds;
        if (!fallbackList.length) return d;

        const currentKey = d.kind || "";
        const stillValid = fallbackList.some((k) => k.key === currentKey);

        if (stillValid) return d;

        // Otherwise, pick appropriate default based on target
        let newKindKey = fallbackList[0].key;

        if (d.target === "company") {
          const companyPreferred = ["certificate_of_incorporation", "articles_of_association", "business_license"];
          const hit = fallbackList.find((k) => companyPreferred.includes(k.key)) || fallbackList[0];
          newKindKey = hit.key;
        } else if (d.target === "ubo") {
          const uboPreferred = ["passport", "national_id", "drivers_license"];
          const hit = fallbackList.find((k) => uboPreferred.includes(k.key)) || fallbackList[0];
          newKindKey = hit.key;
        }

        return { ...d, kind: newKindKey };
      }),
    );
  }, [meta.docKinds]);

  // sectors list as strings, ensure "Other"
  const COMMON = ["Professional Services", "Holding/Investment", "Real Estate", "Technology", "Finance", "Gaming & Entertainment", "Other"];
  const sectors = useMemo(() => meta.sectors || [], [meta.sectors]);

  /* ======= helpers */
  const updateCompany = (patch) => setCompany((c) => ({ ...c, ...patch }));

  const addEmail = () => {
    const v = emailInput.trim();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setEmailError("Invalid email");
      notify("Invalid email");
      return;
    }
    setEmailError("");
    setEmails((list) => Array.from(new Set([...list, v])));
    setEmailInput("");
  };
  const removeEmail = (i) => setEmails((list) => list.filter((_, idx) => idx !== i));

  // Cached + race-safe UBO search
  const searchExistingUbos = useCallback(async (firstName, lastName, country) => {
    const f = (firstName || "").trim();
    const l = (lastName || "").trim();
    const c = (country || "").trim();

    // Thresholds: require country, and enough characters
    const combined = `${f}${l}`;
    if (!c || (f.length < MIN_FIRST_OR_LAST && l.length < MIN_FIRST_OR_LAST) || combined.length < MIN_COMBINED) {
      return [];
    }

    const key = `${c}|${f.toLowerCase()}|${l.toLowerCase()}`;
    const cached = uboSearchCacheRef.current.get(key);
    if (cached) return cached;

    // Race-safety: tag this request, ignore stale responses
    const seq = ++uboSearchSeqRef.current;

    try {
      const params = new URLSearchParams({ first_name: f, last_name: l, country: c });
      const response = await fetchJSON(`${API}/persons/search?${params}`);
      const persons = Array.isArray(response?.persons) ? response.persons : [];

      // Only accept if this is the latest request seen
      if (seq >= uboLatestHandledRef.current) {
        uboLatestHandledRef.current = seq;
        uboSearchCacheRef.current.set(key, persons);
      }

      return persons;
    } catch (error) {
      console.warn("UBO search failed:", error);
      return [];
    }
  }, []);

  const addUbo = () => setUbos((u) => [...u, emptyUbo()]);

  const removeUbo = (i) => setUbos((u) => u.filter((_, j) => j !== i));

  const updateUbo = useCallback(
    (i, patch) => {
      // 1) Update the row immediately (unchanged behavior)
      setUbos((u) => {
        const next = u.slice();
        next[i] = { ...next[i], ...patch };
        return next;
      });

      // 2) Debounced lookup when relevant fields change
      if (patch.first_name !== undefined || patch.last_name !== undefined || patch.country_iso2 !== undefined) {
        // Clear any pending timer for this row
        const t = uboSearchTimersRef.current.get(i);
        if (t) {
          clearTimeout(t);
          uboSearchTimersRef.current.delete(i);
        }

        // Snapshot the latest row after state update (use functional read)
        const schedule = setTimeout(async () => {
          let uboSnapshot;
          setUbos((u) => {
            uboSnapshot = { ...u[i] };
            return u;
          });

          // Skip if already linked to an existing person
          if (uboSnapshot?._is_existing) return;

          const f = (uboSnapshot?.first_name || "").trim();
          const l = (uboSnapshot?.last_name || "").trim();
          const c = (uboSnapshot?.country_iso2 || "").trim();
          const combined = `${f}${l}`;

          // Thresholds
          if (!c || (f.length < MIN_FIRST_OR_LAST && l.length < MIN_FIRST_OR_LAST) || combined.length < MIN_COMBINED) {
            return;
          }

          try {
            const suggestions = await searchExistingUbos(f, l, c);
            if (Array.isArray(suggestions) && suggestions.length > 0) {
              setUboSuggestions(suggestions);
              setCurrentUboIndex(i);
              setShowUboModal(true);
            }
          } catch (e) {
            console.warn("UBO search failed:", e);
          }
        }, UBO_SEARCH_DEBOUNCE);

        uboSearchTimersRef.current.set(i, schedule);
      }
    },
    [searchExistingUbos],
  );

  const handleUseExistingUbo = (existingPerson) => {
    if (currentUboIndex !== null) {
      setUbos((u) => {
        const next = u.slice();
        next[currentUboIndex] = {
          ...existingPerson,
          share_pct: next[currentUboIndex].share_pct, // Keep current share %
          start_date: next[currentUboIndex].start_date,
          end_date: next[currentUboIndex].end_date,
          _is_existing: true,
          _existing_id: existingPerson.id,
        };
        return next;
      });
    }
    setShowUboModal(false);
    setCurrentUboIndex(null);
    setUboSuggestions([]);
  };

  const handleCreateNewUbo = () => {
    if (currentUboIndex !== null) {
      setUbos((u) => {
        const next = u.slice();
        next[currentUboIndex] = { ...next[currentUboIndex], _is_existing: false, _existing_id: null };
        return next;
      });
    }
    setShowUboModal(false);
    setCurrentUboIndex(null);
    setUboSuggestions([]);
  };

  const updateDoc = (index, patch) => {
    setDocs((docs) => {
      const next = [...docs];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const removeDoc = (index) => {
    setDocs((docs) => docs.filter((_, i) => i !== index));
  };

  const addDoc = () => {
    const allowedCompany = getAllowedDocKinds(meta.docKinds || [], "company");
    const defaultKind = allowedCompany[0]?.key || "passport";
    setDocs((d) => [...d, emptyDoc(defaultKind, "company")]);
  };

  /* ======= validation for step navigation */
  const entityValid = useMemo(() => {
    const base =
      company.name &&
      company.country_iso2 &&
      company.sector &&
      (company.sector !== "other" || company.sector_other) &&
      company.legal_form &&
      (company.legal_form !== "other" || company.legal_form_other);
    return !!base;
  }, [company]);

  const { errors: uboErrors, total: uboTotal } = useMemo(() => validateUbos(ubos), [ubos]);

  const docsErrors = useMemo(() => validateDocuments(docs, ubos, company, meta), [docs, ubos, company, meta]);

  const docsValid = useMemo(() => docsErrors.length === 0, [docsErrors]);

  /* ======= Navigation handlers (separate from submission) */
  const handleNext = () => {
    const nextStep = step + 1;

    if (step === 0 && !entityValid) {
      setMsg("Please complete the required fields in Entity.");
      return;
    }
    if (step === 1 && uboErrors.length) {
      setMsg(uboErrors.map((e) => e.message).join("\n"));
      return;
    }
    if (step === 2 && !docsValid) {
      setMsg(docsErrors.map((e) => e.message).join("\n"));
      return;
    }

    setMsg("");
    setStep(nextStep);
  };

  const handleBack = () => {
    setMsg("");
    setStep(Math.max(0, step - 1));
  };

  const gotoStep = (targetStep) => {
    if (targetStep > step) {
      // Can only go forward if current step is valid
      if (step === 0 && !entityValid) {
        setMsg("Please complete the required fields in Entity.");
        return;
      }
      if (step === 1 && uboErrors.length) {
        setMsg(uboErrors.map((e) => e.message).join("\n"));
        return;
      }
      if (step === 2 && !docsValid) {
        setMsg(docsErrors.map((e) => e.message).join("\n"));
        return;
      }
    }
    setMsg("");
    setStep(targetStep);
  };

  /* ======= Submit handler (only for final submission) */
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return; // Prevent double submission

    isSubmittingRef.current = true;
    setMsg("");
    setCreated(null);

    // Final validation
    if (!entityValid) {
      setStep(0);
      setMsg("Please complete the required fields in Entity.");
      isSubmittingRef.current = false;
      return;
    }
    if (uboErrors.length) {
      setStep(1);
      setMsg(uboErrors.map((e) => e.message).join("\n"));
      isSubmittingRef.current = false;
      return;
    }
    if (!docsValid) {
      setStep(2);
      setMsg(docsErrors.map((e) => e.message).join("\n"));
      isSubmittingRef.current = false;
      return;
    }

    const payload = {
      company: stripEmpty({
        ...company,
        // company.legal_form and company.sector are already keys ("cv", "finance", "other", ...)
        emails: emails.length ? emails : undefined,
      }),
      ubos: ubos
        .filter((u) => u.first_name || u.last_name)
        .map((u) =>
          stripEmpty({
            first_name: u.first_name,
            last_name: u.last_name,
            country_iso2: u.country_iso2,
            share_pct: u.share_pct ? Number(u.share_pct) : undefined,
            start_date: u.start_date,
            end_date: u.end_date,
            existing_person_id: u._is_existing ? u._existing_id : undefined,
          }),
        ),
      documents: docs.map((d) => {
        const metaOnly = stripEmpty({ ...d });
        delete metaOnly.file;
        metaOnly.ubo_index = d.target === "ubo" ? Number(d.ubo_index || 0) : null;
        metaOnly.kind = d.kind || "passport"; // canonical key
        return metaOnly;
      }),
    };

    try {
      const res = await createIntake(payload);
      setCreated(res.company_id);
      setMsg("Saved");

      // upload files after company & persons exist
      const statuses = [];
      for (let i = 0; i < docs.length; i++) {
        const d = docs[i];
        if (!d.file) {
          statuses[i] = "skipped";
          continue;
        }
        const fd = { ...stripEmpty(d), file: d.file };
        const personId = d.target === "ubo" ? res.ubo_person_ids?.[Number(d.ubo_index || 0)] : null;
        try {
          await addCompanyDoc(res.company_id, fd, personId || null);
          statuses[i] = "uploaded";
        } catch {
          statuses[i] = "failed";
        }
      }
      setUploadStatus(statuses);

      // Clear the saved draft after successful submission
      sessionStorage.removeItem(INTAKE_STORAGE_KEY);
      setSaveStatus("saved");
    } catch (err) {
      if (err.missingDocs && err.missingDocs.length > 0) {
        setMsg(`Missing required documents: ${err.missingDocs.join(", ")}`);
        setStep(2);
      } else {
        setMsg(err.message || "Failed to save intake");
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  /* ======= select options */
  // Countries: objects from API (explicit mapping)
  const countryOptions = (meta.countries || []).map((c, i) => {
    const key = c.iso2 || c.id || `country-${i}`;
    const val = c.iso2 || c.id || "";
    const label = c.name || key;
    return (
      <option key={key} value={val}>
        {label}
      </option>
    );
  });

  /* ======= UI */
  return (
    <ErrorBoundary componentName="IntakePage">
      <div className="container">
        <h1 className="page-title">Intake</h1>

        <div className="card" style={{ padding: 16 }}>
          {/* Wizard head */}
          <div className="wizard-head">
            <div className="wizard-title">
              Create a new company
              <SaveStatus status={saveStatus} />
            </div>
            <div className="wizard-progress" aria-label={`Progress ${Math.round(progress)}%`}>
              <div className="wizard-progress-bar" style={{ width: `${progress}%` }} aria-hidden />
              <div className="wizard-progress-text">
                Step {step + 1} of {STEPS.length}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            {STEPS.map((label, i) => (
              <button key={label} type="button" className={`tab-btn ${i === step ? "active" : ""}`} onClick={() => gotoStep(i)} disabled={loadingMeta && i > 0}>
                {label}
              </button>
            ))}
          </div>

          {/* Content - NO FORM WRAPPER */}
          <div>
            {/* ===== Section A: Entity ===== */}
            {step === 0 && (
              <section className="form-section">
                <h3>Entity</h3>
                <div className="form-grid-2">
                  <Field label="Name *">
                    <input
                      value={company.name}
                      onChange={(e) => updateCompany({ name: e.target.value })}
                      placeholder="Registered name"
                      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                    />
                  </Field>
                  <Field label="Alias">
                    <input
                      value={company.alias}
                      onChange={(e) => updateCompany({ alias: e.target.value })}
                      placeholder="Trading name (optional)"
                      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                    />
                  </Field>

                  <Field label="Country *">
                    <select value={company.country_iso2} onChange={(e) => updateCompany({ country_iso2: e.target.value })}>
                      <option value="" />
                      {countryOptions}
                    </select>
                  </Field>

                  <Field label="Sector *">
                    <select value={company.sector || ""} onChange={(e) => updateCompany({ sector: e.target.value, sector_other: "" })}>
                      <option value="" />
                      {(meta.sectors || []).map((s, i) => safeOption(s, i, "sector"))}
                    </select>
                  </Field>

                  {company.sector === "other" && (
                    <Field label="Specify sector">
                      <input
                        value={company.sector_other || ""}
                        onChange={(e) => updateCompany({ sector_other: e.target.value })}
                        placeholder="e.g., eGaming platform operator"
                        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                      />
                    </Field>
                  )}

                  <Field label="Legal form *">
                    <select value={company.legal_form || ""} onChange={(e) => updateCompany({ legal_form: e.target.value, legal_form_other: "" })}>
                      <option value="" />
                      {(meta.legal_forms || []).map((l, i) => safeOption(l, i, "legal_form"))}
                    </select>
                  </Field>

                  {company.legal_form === "other" && (
                    <Field label="Specify legal form">
                      <input
                        value={company.legal_form_other || ""}
                        onChange={(e) => updateCompany({ legal_form_other: e.target.value })}
                        placeholder="e.g., Special Purpose Vehicle"
                        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                      />
                    </Field>
                  )}

                  <Field label="Residency">
                    <select value={company.residency_status} onChange={(e) => updateCompany({ residency_status: e.target.value })}>
                      <option value="local">local</option>
                      <option value="international">international</option>
                      <option value="foreign">foreign</option>
                    </select>
                  </Field>

                  {/* ✅ NEW FIELD - Insert after residency_status */}
                  <Field label="Managed by *" hint="Select the management company for this entity">
                    <select
                      value={company.managed_by || "gtrust"}
                      onChange={(e) => updateCompany({ managed_by: e.target.value })}
                      aria-label="Management company"
                      aria-required="true">
                      <option value="gtrust">G-Trust Curaçao B.V.</option>
                      <option value="prostaff">ProStaff Curaçao N.V.</option>
                    </select>
                  </Field>

                  <Field label="CoC number(KvK)">
                    <input
                      value={company.registration_no}
                      onChange={(e) => updateCompany({ registration_no: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                    />
                  </Field>

                  <Field label="Email(s)">
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <input
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        placeholder="info@example.com"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addEmail();
                          }
                        }}
                        aria-label="Add company email"
                      />
                      <button type="button" onClick={addEmail}>
                        Add
                      </button>
                      {emails.map((em, i) => (
                        <span key={em} className="chip">
                          {em}
                          <button type="button" onClick={() => removeEmail(i)} aria-label="remove email">
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    {emailError && <div className="error-message">{emailError}</div>}
                  </Field>

                  <Field label="Phone">
                    <input
                      value={company.phone}
                      onChange={(e) => updateCompany({ phone: e.target.value })}
                      placeholder="+599 ..."
                      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                    />
                  </Field>

                  <Field label="Registered address">
                    <textarea rows={2} value={company.address} onChange={(e) => updateCompany({ address: e.target.value })} placeholder="Street, number, city, postal code" />
                  </Field>

                  <div style={{ gridColumn: "1 / -1" }}>
                    <Field label="Notes">
                      <textarea value={company.notes} onChange={(e) => updateCompany({ notes: e.target.value })} />
                    </Field>
                  </div>
                </div>
              </section>
            )}

            {/* ===== Section B: UBOs ===== */}
            {step === 1 && (
              <section className="form-section">
                <h3>UBOs</h3>
                {ubos.length === 0 && (
                  <div className="text-muted" style={{ marginBottom: 12 }}>
                    No UBOs added yet.
                  </div>
                )}
                {ubos.map((u, i) => (
                  <div
                    key={i}
                    className="card"
                    style={{
                      padding: 12,
                      marginBottom: 12,
                      backgroundColor: u._is_existing ? "#f0f8ff" : "inherit",
                      border: u._is_existing ? "1px solid #4a90e2" : "inherit",
                    }}>
                    {u._is_existing && (
                      <div className="text-muted" style={{ fontSize: 12, marginBottom: 8, color: "#4a90e2" }}>
                        Using existing person (ID: {u._existing_id})
                      </div>
                    )}
                    <div className="form-grid-2">
                      <Field label="First name">
                        <input value={u.first_name} onChange={(e) => updateUbo(i, { first_name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && e.preventDefault()} />
                      </Field>
                      <Field label="Last name">
                        <input value={u.last_name} onChange={(e) => updateUbo(i, { last_name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && e.preventDefault()} />
                      </Field>
                      <Field label="Country">
                        <select value={u.country_iso2} onChange={(e) => updateUbo(i, { country_iso2: e.target.value })}>
                          <option value="" />
                          {(meta.countries || []).map((c, idx) => {
                            const key = c.iso2 || c.id || `country-${idx}`;
                            const val = c.iso2 || c.id || "";
                            const label = c.name || key;
                            return (
                              <option key={key} value={val}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </Field>
                      <Field label="Share %">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={u.share_pct}
                          onChange={(e) => updateUbo(i, { share_pct: e.target.value })}
                          onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                        />
                      </Field>
                      <Field label="Start date">
                        <input type="date" value={u.start_date} onChange={(e) => updateUbo(i, { start_date: e.target.value })} />
                      </Field>
                      <Field label="End date">
                        <input type="date" value={u.end_date} onChange={(e) => updateUbo(i, { end_date: e.target.value })} />
                      </Field>
                    </div>
                    <div className="wizard-footer" style={{ marginTop: 8 }}>
                      <button type="button" className="secondary" onClick={() => removeUbo(i)} aria-label={`remove ubo ${i}`}>
                        Remove UBO
                      </button>
                    </div>
                  </div>
                ))}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                  <button type="button" onClick={addUbo}>
                    Add UBO
                  </button>
                  <div className="text-muted">
                    Total share %: <b style={{ color: uboTotal > 100 ? "#b00020" : "inherit" }}>{Number.isFinite(uboTotal) ? uboTotal.toFixed(2) : "0"}</b>
                  </div>
                </div>
                {!!uboErrors.length && (
                  <div className="error-message" style={{ marginTop: 8 }}>
                    {uboErrors.map((e, idx) => (
                      <div key={idx}>{e.message}</div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ===== Section C: Documents ===== */}
            {step === 2 && (
              <section className="form-section">
                <h3>Documents</h3>
                {docs.map((d, i) => {
                  const allowedKinds = getAllowedDocKinds(meta.docKinds || [], d.target);
                  const allKinds = allowedKinds.length ? allowedKinds : meta.docKinds || [];
                  const kindKey = d.kind || allKinds[0]?.key || "passport";
                  const cfg = meta.docKindFields?.[kindKey] || { required: [], optional: [] };
                  const fields = [...(cfg.required || []), ...(cfg.optional || [])];

                  return (
                    <div key={i} className="card" style={{ padding: 12, marginBottom: 12 }}>
                      <div className="form-grid-2">
                        <Field label="Target">
                          <div style={{ display: "flex", gap: 8 }}>
                            <select value={d.target} onChange={(e) => updateDoc(i, { target: e.target.value })}>
                              <option value="company">company</option>
                              <option value="ubo">ubo</option>
                            </select>
                            {d.target === "ubo" && (
                              <select value={d.ubo_index ?? 0} onChange={(e) => updateDoc(i, { ubo_index: Number(e.target.value) })}>
                                {ubos.map((_, idx) => (
                                  <option key={idx} value={idx}>
                                    {idx}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        </Field>

                        <Field label="Kind">
                          <select value={kindKey} onChange={(e) => updateDoc(i, { kind: e.target.value })} disabled={loadingMeta || allKinds.length === 0}>
                            {allKinds.map((k, idx) => safeOption(k, idx, "doc_kind"))}
                          </select>
                        </Field>

                        {kindKey === "other" && (
                          <Field label="Specify kind">
                            <input
                              value={d.kind_label || ""}
                              onChange={(e) => updateDoc(i, { kind_label: e.target.value })}
                              placeholder="e.g., Gaming license approval"
                              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                            />
                          </Field>
                        )}

                        {fields.map((f) => {
                          if (f === "status") {
                            return (
                              <Field key={f} label="Status">
                                <select value={d.status || ""} onChange={(e) => updateDoc(i, { status: e.target.value })}>
                                  <option value="" />
                                  {meta.docStatuses.map((st, idx) => safeOption(st, idx, "doc_status"))}
                                </select>
                              </Field>
                            );
                          }
                          if (f === "file") {
                            return (
                              <Field key={f} label="File">
                                <input type="file" onChange={(e) => updateDoc(i, { file: e.target.files?.[0] || null })} />
                              </Field>
                            );
                          }
                          const isDate = f.includes("date") || f.endsWith("_on") || f.startsWith("period_");
                          return (
                            <Field key={f} label={f}>
                              <input
                                type={isDate ? "date" : "text"}
                                value={d[f] || ""}
                                onChange={(e) => updateDoc(i, { [f]: e.target.value })}
                                onKeyDown={!isDate ? (e) => e.key === "Enter" && e.preventDefault() : undefined}
                              />
                            </Field>
                          );
                        })}
                      </div>

                      <div className="wizard-footer" style={{ marginTop: 8 }}>
                        <button type="button" className="secondary" onClick={() => removeDoc(i)}>
                          Remove document
                        </button>
                        {uploadStatus[i] && (
                          <span className="text-muted" style={{ marginLeft: 8 }}>
                            {uploadStatus[i]}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {!!docsErrors.length && (
                  <div className="error-message" style={{ marginTop: 8 }}>
                    {docsErrors.map((e, idx) => (
                      <div key={idx}>{e.message}</div>
                    ))}
                  </div>
                )}
                <button type="button" onClick={addDoc}>
                  Add Document
                </button>
              </section>
            )}

            {/* ===== Section D: Review & Submit ===== */}
            {step === 3 && (
              <section className="form-section">
                <h3>Review & Submit</h3>

                {(() => {
                  const errs = [...uboErrors, ...docsErrors];
                  return errs.length ? (
                    <div className="card" style={{ padding: 12, marginBottom: 12, borderColor: "#cc4444" }}>
                      <h4 style={{ marginBottom: 8 }}>Issues to fix before activation</h4>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {errs.map((e, i) => (
                          <li key={i}>{e.message}</li>
                        ))}
                      </ul>
                      <div className="text-muted" style={{ marginTop: 8 }}>
                        You can save now (status <b>pending</b>). Activation requires these to be resolved.
                      </div>
                    </div>
                  ) : null;
                })()}

                <div className="card" style={{ padding: 12, marginBottom: 12 }}>
                  <h4 style={{ marginBottom: 12 }}>Entity</h4>
                  <div className="detail-grid">
                    <div>
                      <b>Name</b>
                      <div>{company.name || "–"}</div>
                    </div>
                    <div>
                      <b>Alias</b>
                      <div>{company.alias || "–"}</div>
                    </div>
                    <div>
                      <b>Country</b>
                      <div>{company.country_iso2 || "–"}</div>
                    </div>
                    <div>
                      <b>Sector</b>
                      <div>{company.sector === "other" ? company.sector_other || "Other" : findLabelInList(meta.sectors, company.sector) || "–"}</div>
                    </div>
                    <div>
                      <b>Legal form</b>
                      <div>{company.legal_form === "other" ? company.legal_form_other || "Other" : findLabelInList(meta.legal_forms, company.legal_form) || "–"}</div>
                    </div>
                    <div>
                      <b>Residency</b>
                      <div>{company.residency_status || "–"}</div>
                    </div>
                    {/* ✅ NEW: Show managed_by in review */}
                    <div>
                      <b>Managed by</b>
                      <div>{company.managed_by === "prostaff" ? "ProStaff Curaçao N.V." : company.managed_by === "gtrust" ? "G-Trust Curaçao B.V." : "–"}</div>
                    </div>
                    <div>
                      <b>Reg. no.</b>
                      <div>{company.registration_no || "–"}</div>
                    </div>
                    <div>
                      <b>Emails</b>
                      <div>{emails.length ? emails.join(", ") : "–"}</div>
                    </div>
                    <div>
                      <b>Phone</b>
                      <div>{company.phone || "–"}</div>
                    </div>
                  </div>
                  {company.address && (
                    <div className="text-muted" style={{ marginTop: 8 }}>
                      <b>Address</b>
                      <div>{company.address}</div>
                    </div>
                  )}
                  {company.notes && (
                    <div className="text-muted" style={{ marginTop: 8 }}>
                      <b>Notes</b>
                      <div>{company.notes}</div>
                    </div>
                  )}
                </div>

                <div className="card" style={{ padding: 12, marginBottom: 12 }}>
                  <h4 style={{ marginBottom: 12 }}>UBOs ({ubos.length})</h4>
                  {ubos.length === 0 ? (
                    <div className="text-muted">No UBOs provided.</div>
                  ) : (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>First</th>
                            <th>Last</th>
                            <th>Country</th>
                            <th>Share %</th>
                            <th>Start</th>
                            <th>End</th>
                            <th>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ubos.map((u, i) => (
                            <tr key={i}>
                              <td>{i}</td>
                              <td>{u.first_name || "–"}</td>
                              <td>{u.last_name || "–"}</td>
                              <td>{u.country_iso2 || "–"}</td>
                              <td>{u.share_pct || "–"}</td>
                              <td>{u.start_date || "–"}</td>
                              <td>{u.end_date || "–"}</td>
                              <td>{u._is_existing ? "Existing" : "New"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="card" style={{ padding: 12, marginBottom: 12 }}>
                  <h4 style={{ marginBottom: 12 }}>Documents ({docs.length})</h4>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Target</th>
                          <th>UBO idx</th>
                          <th>Kind</th>
                          <th>Identifier</th>
                          <th>Issued</th>
                          <th>Expires</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docs.map((d, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{d.target}</td>
                            <td>{d.target === "ubo" ? d.ubo_index : "–"}</td>
                            <td>{safeText(d.kind) === "other" ? d.kind_label || "other" : safeText(d.kind)}</td>
                            <td>{d.identifier || "–"}</td>
                            <td>{d.period_from || d.issued_on || "–"}</td>
                            <td>{d.period_to || d.expires_on || "–"}</td>
                            <td>{uploadStatus[i] || d.status || "–"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {created && <div className="success-message">Created company #{created}</div>}
                {msg && !created && <div className="error-message">{msg}</div>}
              </section>
            )}

            {/* Wizard footer */}
            <div className="wizard-footer">
              <button type="button" className="secondary" disabled={step === 0} onClick={handleBack} aria-label="Back">
                ← Back
              </button>

              {step < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={(step === 0 && !entityValid) || (step === 1 && !!uboErrors.length) || (step === 2 && !docsValid)}
                  aria-label="Next">
                  Next →
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} disabled={isSubmittingRef.current}>
                  {isSubmittingRef.current ? "Saving..." : "Save Intake"}
                </button>
              )}
            </div>

            {msg && step !== 3 && (
              <div className="error-message" style={{ marginTop: 12 }}>
                {msg}
              </div>
            )}
          </div>
        </div>

        {/* UBO Suggestion Modal */}
        <UboSuggestionModal
          isOpen={showUboModal}
          suggestions={uboSuggestions}
          onClose={() => setShowUboModal(false)}
          onUseExisting={handleUseExistingUbo}
          onCreateNew={handleCreateNewUbo}
        />
      </div>
    </ErrorBoundary>
  );
}
